import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { LayoutTemplate, X, Trash2, Save } from 'lucide-react';
import { KIND_META } from './KitItemPicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/tool-crib`;

export function useTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    try { const r = await axios.get(`${API}/templates`); setTemplates(r.data.templates || []); }
    catch { setTemplates([]); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);
  return { templates, loading, reload: load };
}

// Dropdown used on the "New kit" row: start the list from a template in one tap.
export function TemplateSelect({ value, onChange }) {
  const { templates } = useTemplates();
  if (!templates.length) return null;
  return (
    <select value={value} onChange={e => onChange(e.target.value)}
      className="px-3 py-2.5 rounded-xl bg-white text-slate-900 text-sm outline-none focus:ring-2 focus:ring-emerald-400 max-w-[14rem]"
      title="Start the new kit from a saved template" data-testid="kit-new-template">
      <option value="">Blank list</option>
      {templates.map(t => <option key={t.id} value={t.id}>{t.name} ({t.item_count})</option>)}
    </select>
  );
}

const TemplateRow = ({ t, onPick }) => (
  <button onClick={() => onPick(t)} className="w-full text-left rounded-xl bg-white hover:bg-emerald-50 border border-slate-200 px-4 py-3 flex items-center justify-between gap-3 min-h-[3.5rem]"
    data-testid={`kit-template-${t.id}`}>
    <div className="min-w-0">
      <p className="font-bold text-slate-900 truncate">{t.name}</p>
      <p className="text-xs text-slate-500 truncate">{t.item_count} item{t.item_count !== 1 ? 's' : ''}{t.description ? ` · ${t.description}` : ''}{t.use_count ? ` · used ${t.use_count}×` : ''}</p>
    </div>
    <span className="text-xs font-semibold text-emerald-700 whitespace-nowrap">Use ▸</span>
  </button>
);

// Pick a template to add into the open kit. `inline` renders the list directly (empty-kit state).
export function TemplatePicker({ onPick, onClose, inline }) {
  const { templates, loading } = useTemplates();
  const body = (
    <div className="space-y-2" data-testid="kit-template-picker">
      {loading ? <p className="text-sm text-slate-500">Loading…</p>
        : templates.length === 0 ? <p className="text-sm text-slate-600 bg-white/80 rounded-xl p-3">No templates yet — build a kit, then tap <b>Save as template</b>.</p>
        : templates.map(t => <TemplateRow key={t.id} t={t} onPick={onPick} />)}
    </div>
  );
  if (inline) return <div className="max-w-md mx-auto text-left">{body}</div>;
  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-end sm:items-center justify-center p-3" onClick={onClose}>
      <div className="bg-slate-100 rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto p-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-slate-900 flex items-center gap-2"><LayoutTemplate className="w-5 h-5" /> Start from a template</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-200" data-testid="kit-template-picker-close"><X className="w-5 h-5" /></button>
        </div>
        {body}
      </div>
    </div>
  );
}

export function useSaveTemplate(kit) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const save = async (name, description, overwrite = false) => {
    setSaving(true);
    try {
      const r = await axios.post(`${API}/templates`, { name, description, from_kit_id: kit.id, overwrite });
      toast.success(`${r.data.overwritten ? 'Updated' : 'Saved'} template "${r.data.name}" (${r.data.item_count} items)`);
      setOpen(false);
      return true;
    } catch (e) {
      if (e.response?.status === 409 && !overwrite) {
        if (window.confirm(`${e.response.data.detail}\n\nOverwrite it with this kit's list?`)) { setSaving(false); return save(name, description, true); }
      } else toast.error(e.response?.data?.detail || 'Could not save');
    }
    setSaving(false);
    return false;
  };
  return { open, setOpen, save, saving };
}

export function SaveTemplateDialog({ ctl }) {
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  if (!ctl.open) return null;
  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-3" onClick={() => ctl.setOpen(false)}>
      <div className="bg-white rounded-2xl w-full max-w-md p-5" onClick={e => e.stopPropagation()} data-testid="kit-save-template-dialog">
        <h3 className="font-bold text-slate-900 flex items-center gap-2"><Save className="w-5 h-5" /> Save this list as a template</h3>
        <p className="text-sm text-slate-600 mt-1">Quantities are kept; packed/returned counts are not. Next time, start a kit from it in one tap.</p>
        <input autoFocus value={name} onChange={e => setName(e.target.value)} placeholder='Template name — e.g. "Conveyor job"'
          className="mt-3 w-full px-3 py-3 rounded-xl border border-slate-300 text-slate-900 outline-none focus:ring-2 focus:ring-emerald-400" data-testid="kit-template-name" />
        <input value={desc} onChange={e => setDesc(e.target.value)} placeholder="Short note (optional)"
          className="mt-2 w-full px-3 py-2.5 rounded-xl border border-slate-300 text-slate-900 text-sm outline-none focus:ring-2 focus:ring-emerald-400" data-testid="kit-template-desc" />
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={() => ctl.setOpen(false)} className="px-4 py-2.5 rounded-xl bg-slate-100 text-slate-800 font-semibold" data-testid="kit-template-cancel">Cancel</button>
          <button onClick={() => ctl.save(name.trim(), desc.trim())} disabled={!name.trim() || ctl.saving}
            className="px-4 py-2.5 rounded-xl bg-emerald-600 disabled:bg-slate-300 text-white font-bold" data-testid="kit-template-save">{ctl.saving ? 'Saving…' : 'Save template'}</button>
        </div>
      </div>
    </div>
  );
}

// Rename / delete templates and peek at what's in them.
export function TemplateManager({ onClose }) {
  const { templates, loading, reload } = useTemplates();
  const [openId, setOpenId] = useState(null);
  const rename = async (t) => {
    const name = window.prompt('Template name', t.name);
    if (!name || name.trim() === t.name) return;
    try { await axios.put(`${API}/templates/${t.id}`, { name: name.trim() }); toast.success('Renamed'); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not rename'); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"? Existing kits are not affected.`)) return;
    try { await axios.delete(`${API}/templates/${t.id}`); toast.success('Template deleted'); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not delete'); }
  };
  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-end sm:items-center justify-center p-3" onClick={onClose}>
      <div className="bg-slate-100 rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto p-4" onClick={e => e.stopPropagation()} data-testid="kit-template-manager">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-slate-900 flex items-center gap-2"><LayoutTemplate className="w-5 h-5" /> Kit templates</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-200" data-testid="kit-template-manager-close"><X className="w-5 h-5" /></button>
        </div>
        {loading ? <p className="text-sm text-slate-500">Loading…</p>
          : templates.length === 0 ? <p className="text-sm text-slate-600 bg-white rounded-xl p-4">No templates yet. Open any kit and tap <b>Save as template</b>.</p>
          : templates.map(t => (
            <div key={t.id} className="bg-white rounded-xl border border-slate-200 mb-2" data-testid={`kit-template-row-${t.id}`}>
              <div className="flex items-center justify-between gap-3 px-4 py-3">
                <button onClick={() => setOpenId(openId === t.id ? null : t.id)} className="text-left min-w-0 flex-1">
                  <p className="font-bold text-slate-900 truncate">{t.name}</p>
                  <p className="text-xs text-slate-500">{t.item_count} items{t.use_count ? ` · used ${t.use_count}×` : ''}{t.updated_by ? ` · by ${t.updated_by}` : ''}</p>
                </button>
                <button onClick={() => rename(t)} className="px-3 py-2 rounded-lg bg-slate-100 text-slate-800 text-xs font-semibold" data-testid={`kit-template-rename-${t.id}`}>Rename</button>
                <button onClick={() => remove(t)} className="p-2 rounded-lg bg-red-50 text-red-700" title="Delete template" data-testid={`kit-template-delete-${t.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
              {openId === t.id && (
                <ul className="px-4 pb-3 grid sm:grid-cols-2 gap-1 text-sm text-slate-800" data-testid={`kit-template-items-${t.id}`}>
                  {(t.items || []).map((it, i) => {
                    const Icon = KIND_META[it.kind]?.icon;
                    return <li key={i} className="flex items-center gap-2">{Icon && <Icon className="w-3.5 h-3.5 text-slate-500" />}<span className="truncate">{it.name}</span><span className="text-slate-500 ml-auto">×{it.qty}</span></li>;
                  })}
                </ul>
              )}
            </div>
          ))}
      </div>
    </div>
  );
}
